/**
 * Sidebar workspace zones (Ads / SEO & GEO).
 *
 * A "nav workspace" is a job-to-be-done whose rail rows swap wholesale; the
 * shared rows (Chat, Automations, Autopilot, Files, Setup) stay put. This is
 * the one structural addition the GEO workspace makes on top of the shipped
 * rail, and it only exists behind the org-level Labs opt-in
 * (`geoWorkspaceOptedIn`): with the flag off there is exactly one zone and no
 * switcher, so the rail renders as it did before.
 *
 * NOTE ON NAMING: `workspace` already means *organization / brand* in this
 * codebase (`lib/brand.ts`, `useBrandWorkspaces`) — WHOSE data. This module is
 * about WHICH JOB, hence the `nav-` prefix on every export.
 */
'use client'

import * as React from 'react'

export type NavWorkspaceId = 'ads' | 'seo-geo'

export type NavWorkspaceItemKey =
  | 'dashboard'
  | 'adLauncher'
  | 'creatives'
  | 'visibility'
  | 'citations'
  | 'reports'
  | 'sites'

export type NavWorkspaceItem = {
  key: NavWorkspaceItemKey
  /** i18n key in the `Sidebar` namespace. */
  labelKey: string
  /** Brand-relative route segment (`/o/{org}/b/{brand}{path}`). */
  path: `/${string}`
  /** Other segments this row stands for (pages reached from it, not rows). */
  alsoOn?: readonly `/${string}`[]
}

export type NavWorkspace = {
  id: NavWorkspaceId
  /** i18n keys in the `Sidebar` namespace. */
  labelKey: string
  descriptionKey: string
  items: NavWorkspaceItem[]
}

export const DEFAULT_NAV_WORKSPACE: NavWorkspaceId = 'ads'

export const NAV_WORKSPACES: readonly NavWorkspace[] = [
  {
    id: 'ads',
    labelKey: 'navWorkspaceAds',
    descriptionKey: 'navWorkspaceAdsDescription',
    items: [
      // The Dashboard is the boards page; its Home board is the report. The
      // ChatGPT Ads campaign console sits under it, switched from the page
      // header (`AdsSectionHeader`).
      {
        key: 'dashboard',
        labelKey: 'dashboard',
        path: '/dashboards',
        alsoOn: ['/campaigns'],
      },
      // The Meta launcher has its own row: under Dashboard → Campaigns a
      // customer looked for it for ten minutes (2026-10-23).
      { key: 'adLauncher', labelKey: 'adLauncher', path: '/ad-launcher' },
      { key: 'creatives', labelKey: 'creatives', path: '/creatives' },
    ],
  },
  {
    id: 'seo-geo',
    labelKey: 'navWorkspaceSeoGeo',
    descriptionKey: 'navWorkspaceSeoGeoDescription',
    items: [
      { key: 'visibility', labelKey: 'visibility', path: '/visibility' },
      { key: 'citations', labelKey: 'citations', path: '/citations' },
      { key: 'reports', labelKey: 'reports', path: '/reports' },
      { key: 'sites', labelKey: 'sites', path: '/sites' },
    ],
  },
]

export function getNavWorkspace(id: NavWorkspaceId): NavWorkspace {
  return NAV_WORKSPACES.find(workspace => workspace.id === id) ?? NAV_WORKSPACES[0]
}

/**
 * Which workspace a brand-scoped pathname unambiguously belongs to, or null
 * for shared surfaces (chat, automations, settings, …). Longest prefix wins so
 * a deep link never leaves the rail contradicting the page it sits beside.
 */
export function workspaceForPath(
  pathname: string | null,
  brandPathBase: string | null,
): NavWorkspaceId | null {
  if (!pathname || !brandPathBase) return null
  if (pathname !== brandPathBase && !pathname.startsWith(`${brandPathBase}/`)) {
    return null
  }
  const sub = pathname.slice(brandPathBase.length)
  let best: { length: number; id: NavWorkspaceId } | null = null
  for (const workspace of NAV_WORKSPACES) {
    for (const item of workspace.items) {
      for (const path of [item.path, ...(item.alsoOn ?? [])]) {
        const hit = sub === path || sub.startsWith(`${path}/`)
        if (hit && (best === null || path.length > best.length)) {
          best = { length: path.length, id: workspace.id }
        }
      }
    }
  }
  return best?.id ?? null
}

// ── Per-browser memory of the last chosen workspace ─────────────────────────
// localStorage-as-external-store, so the server snapshot stays hydration-safe
// (always `ads`) and the stored choice takes over after mount. Not synced to
// the server: cross-device memory is a later concern.

const STORAGE_PREFIX = 'nav-workspace:'
const listeners = new Set<() => void>()

function storageKey(orgSlug: string, brandSlug: string): string {
  return `${STORAGE_PREFIX}${orgSlug}/${brandSlug}`
}

function isNavWorkspaceId(value: unknown): value is NavWorkspaceId {
  return value === 'ads' || value === 'seo-geo'
}

export function readStoredNavWorkspace(
  orgSlug: string,
  brandSlug: string,
): NavWorkspaceId {
  try {
    const raw = window.localStorage.getItem(storageKey(orgSlug, brandSlug))
    return isNavWorkspaceId(raw) ? raw : DEFAULT_NAV_WORKSPACE
  } catch {
    return DEFAULT_NAV_WORKSPACE
  }
}

export function storeNavWorkspace(
  orgSlug: string,
  brandSlug: string,
  id: NavWorkspaceId,
): void {
  try {
    window.localStorage.setItem(storageKey(orgSlug, brandSlug), id)
  } catch {
    // Storage may be unavailable (private mode); the choice then lasts for
    // this render only, which is still correct.
  }
  listeners.forEach(notify => notify())
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange)
  window.addEventListener('storage', onChange)
  return () => {
    listeners.delete(onChange)
    window.removeEventListener('storage', onChange)
  }
}

export function getServerNavWorkspaceSnapshot(): NavWorkspaceId {
  return DEFAULT_NAV_WORKSPACE
}

/** The stored workspace preference for one brand; `ads` until mounted. */
export function useStoredNavWorkspace(
  orgSlug: string | null,
  brandSlug: string | null,
): NavWorkspaceId {
  const getSnapshot = React.useCallback(
    () =>
      orgSlug && brandSlug
        ? readStoredNavWorkspace(orgSlug, brandSlug)
        : DEFAULT_NAV_WORKSPACE,
    [orgSlug, brandSlug],
  )
  return React.useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerNavWorkspaceSnapshot,
  )
}
